import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';

interface LeadData {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  project_type: string;
  property_size?: string;
  budget_range?: string;
  timeline?: string;
  message?: string;
  source_page?: string;
  /** Optional extras used only for the internal notification email. */
  location?: string;
  deliverables?: string;
}

/**
 * Notifies the team by email. Never affects whether the lead was captured:
 * the enquiry is already stored by the time this runs.
 */
const notifyTeam = async (
  leadData: LeadData,
  sourcePage: string,
  leadId?: string,
) => {
  try {
    await supabase.functions.invoke('notify-new-lead', {
      body: {
        lead_id: leadId,
        name: leadData.name,
        email: leadData.email,
        phone: leadData.phone,
        project_type: leadData.project_type,
        location: leadData.location,
        property_size: leadData.property_size,
        deliverables: leadData.deliverables,
        timeline: leadData.timeline,
        message: leadData.message,
        source_page: sourcePage,
      },
    });
  } catch (error) {
    console.error('Lead notification failed (lead was still saved):', error);
  }
};

export const useLeadCapture = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const captureLead = async (leadData: LeadData) => {
    setIsSubmitting(true);
    try {
      // Capture UTM params from URL
      const params = new URLSearchParams(window.location.search);
      const sourcePage = leadData.source_page || window.location.pathname;

      const { data: inserted, error } = await supabase.from('leads').insert({
        name: leadData.name,
        email: leadData.email,
        phone: leadData.phone || null,
        company: leadData.company || null,
        project_type: leadData.project_type,
        property_size: leadData.property_size || null,
        budget_range: leadData.budget_range || null,
        timeline: leadData.timeline || null,
        message: leadData.message || null,
        source_page: sourcePage,
        utm_source: params.get('utm_source') || null,
        utm_medium: params.get('utm_medium') || null,
        utm_campaign: params.get('utm_campaign') || null,
      }).select('id').single();

      if (error) {
        // Some deployments of the leads inbox use an older column layout.
        // Retry with that layout so a real enquiry is never silently lost.
        if (error.code === 'PGRST204') {
          const notes = [
            leadData.message?.trim(),
            leadData.property_size ? `Approx. size: ${leadData.property_size}` : null,
            `Submitted from: ${sourcePage}`,
            params.get('utm_source') ? `Source: ${params.get('utm_source')}` : null,
          ].filter(Boolean).join('\n');

          const { data: legacyInserted, error: legacyError } = await (supabase.from('leads') as any).insert({
            name: leadData.name,
            email: leadData.email,
            phone: leadData.phone || null,
            company: leadData.company || null,
            service_type: leadData.project_type,
            budget_range: leadData.budget_range || null,
            timeline: leadData.timeline || null,
            message: notes || null,
            lead_source: sourcePage,
          }).select('id').single();

          if (legacyError) throw legacyError;
          await notifyTeam(leadData, sourcePage, legacyInserted?.id);
          return true;
        }
        throw error;
      }

      await notifyTeam(leadData, sourcePage, inserted?.id);
      return true;


    } catch (error: any) {
      console.error('Lead capture error:', error);
      toast({
        title: "Something went wrong",
        description: "Please try again or call us at (440) 785-8846.",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { captureLead, isSubmitting };
};
